import {
    createTable,
    PaginationState,
    SearchState,
    SortingState,
    TableHeaderSpec
} from "../../../../../components/table/CreateTable.tsx";
import * as React from "react";
import InfoTooltip from '../../../../../components/general/InfoTooltip.tsx';
import { Link, useNavigate } from "react-router-dom";
import { User } from "../../../../../api/accessControl/User.ts";
import { ClientRow, VatDeclarationColumnState } from "./models.ts";
import { logInAsSubordinate } from "../../../../../utils/OrgManagement.ts";
import { PartnerNoClientsYetEmptyState } from "../../../../../components/specific/PartnerNoClientsYetEmptyState.tsx";
import { ArrowsRightLeftIcon, DocumentTextIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { BankLogoStack } from "./components/clientTable/BankLogoStack.tsx";
import { BankConsentIndicator } from "../../../../../components/general/BankConsentIndicator.tsx";
import { CoverageDonut } from "./components/clientTable/CoverageDonut.tsx";
import { InitialsAvatar } from "./components/clientTable/InitialsAvatar.tsx";
import { ClientRowActionsMenu } from "./components/clientTable/ClientRowActionsMenu.tsx";
import { CellSkeleton } from "./components/clientTable/CellSkeleton.tsx";
import { VatDeclarationStatusPill } from "./components/clientTable/VatDeclarationStatusPill.tsx";
import {
    notStartedWithoutDeclaration,
    VAT_DECLARATION_LOAD_FAILED,
    vatDeclarationSortRank,
} from "./components/clientTable/vatDeclarationStatus.ts";

function NumberCell({ value, loaded }: { value: number; loaded: boolean }) {
    if (!loaded) {
        return <CellSkeleton width={28} />;
    }
    if (value === 0) {
        return <span className="tabular-nums text-gray-300">0</span>;
    }
    return <span className="tabular-nums text-gray-700">{value}</span>;
}

function NumericHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            {icon}
            {label}
        </span>
    );
}

interface Props {
    currentUser: User;
    clientReports: ClientRow[];
    onboardingUrl: string;
    activityLoaded: boolean;
    banksLoaded: boolean;
    vatDeclarationState: VatDeclarationColumnState;
    onClientSettingsClick?: (client: ClientRow) => void;
    paginationState?: [
        paginationState: PaginationState<ClientRow>,
        setPaginationState: (paginationState: PaginationState<ClientRow>) => void,
    ];
    sortingState?: [
        sortingState: SortingState,
        setSortingState: (sortingState: SortingState) => void
    ];
    onQueryUpdate?: (pagination: PaginationState<ClientRow> | null, sorting: SortingState | null) => void;
    filters?: React.ReactNode;
    isFiltered?: boolean;
    actions?: React.ReactNode;
}

function formatEmployees(fteAmount: number): string {
    return `${fteAmount} ${fteAmount === 1 ? 'medewerker' : 'medewerkers'}`;
}

export function ClientsTable({
                                 clientReports,
                                 onboardingUrl,
                                 activityLoaded,
                                 banksLoaded,
                                 vatDeclarationState,
                                 onClientSettingsClick = () => {
                                 },
                                 paginationState,
                                 sortingState,
                                 filters,
                                 isFiltered = false,
                                 actions,
                             }: Props) {

    const navigate = useNavigate();

    if (clientReports.length === 0 && !isFiltered) {
        return <PartnerNoClientsYetEmptyState
            onAddBusinessClick={() => navigate("/add-business")}
            onboardingUrl={onboardingUrl}
        />
    }

    const tableSpec: TableHeaderSpec<ClientRow> = {
        title: `${clientReports.length} Klanten`,
        columns: [
            {
                name: 'Bedrijf',
                field: 'business_name',
                style: {
                    widthMultiplier: 2.5
                },
                customRender: (row: ClientRow) => (
                    <div className="flex items-center gap-3">
                        <InitialsAvatar name={row.business_name} />
                        <div className="min-w-0">
                            <span className="block max-w-full truncate font-medium text-gray-900" title={row.business_name}>
                                {row.business_name}
                            </span>
                            <span className="text-gray-500 text-xs">{formatEmployees(row.fte_amount)}</span>
                        </div>
                    </div>
                )
            },
            {
                name: <NumericHeader icon={<ArrowsRightLeftIcon className="h-4 w-4 text-gray-400" />} label="Transacties" />,
                field: 'transactions_count',
                style: {
                    widthMultiplier: 0.8
                },
                customRender: (row: ClientRow) => (
                    <NumberCell value={row.transactions_count} loaded={activityLoaded} />
                )
            },
            {
                name: <NumericHeader icon={<DocumentTextIcon className="h-4 w-4 text-gray-400" />} label="Documenten" />,
                field: 'documents_uploaded_count',
                style: {
                    widthMultiplier: 0.8
                },
                customRender: (row: ClientRow) => (
                    <NumberCell value={row.documents_uploaded_count} loaded={activityLoaded} />
                )
            },
            {
                name: <NumericHeader icon={<QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />} label="Vraagposten" />,
                field: 'mdr_count',
                style: {
                    widthMultiplier: 0.9
                },
                customRender: (row: ClientRow) => (
                    <NumberCell value={row.mdr_count} loaded={activityLoaded} />
                )
            },
            {
                name: (
                    <>
                        Dekking{' '}
                        <InfoTooltip text="Aandeel transacties met een gekoppeld document (transacties minus vraagposten)." />
                    </>
                ),
                field: 'completeness',
                style: {
                    widthMultiplier: 0.9
                },
                customRender: (row: ClientRow) => {
                    if (!activityLoaded) {
                        return <CellSkeleton width={60} />;
                    }
                    const hasTransactions = row.transactions_count > 0;
                    const completeness = hasTransactions
                        ? ((row.transactions_count - row.mdr_count) / row.transactions_count) * 100
                        : null;
                    return (
                        <div className="flex items-center gap-2">
                            <CoverageDonut percentage={completeness} size={28} strokeWidth={4} showLabel={false} />
                            <span className="tabular-nums text-sm text-gray-600">
                                {completeness == null ? "–" : `${Math.round(completeness)}%`}
                            </span>
                        </div>
                    );
                }
            },
            {
                name: (
                    <>
                        Inzichten{' '}
                        <InfoTooltip text="Het totaal aantal ontvangen inzichten" />
                    </>
                ),
                field: 'total_insights',
                style: {
                    widthMultiplier: 1
                },
                customRender: (row: ClientRow) => (
                    <Link
                        className={
                            "inline-flex items-center gap-1.5 tabular-nums hover:text-primary hover:underline " +
                            (row.total_insights === 0 ? "text-gray-300" : "text-gray-700")
                        }
                        to={`/client-insights/${row.id}/${encodeURIComponent(row.business_name)}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {row.total_insights}
                        {row.has_unread_insights && (
                            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                        )}
                    </Link>
                )
            },
            {
                name: 'Banken',
                field: 'bank_accounts',
                style: {
                    widthMultiplier: 1
                },
                disableSorting: true,
                customRender: (row: ClientRow) => (
                    banksLoaded ? (
                        <BankConsentIndicator bankAccounts={row.bank_accounts}>
                            <BankLogoStack bankAccounts={row.bank_accounts} />
                        </BankConsentIndicator>
                    ) : <CellSkeleton width={60} />
                )
            },
            ...(vatDeclarationState !== 'hidden' ? [{
                name: 'Btw-aangifte',
                field: 'vat_declaration',
                style: {
                    widthMultiplier: 1.2
                },
                sortValue: (row: ClientRow) => vatDeclarationState === 'loaded'
                    ? vatDeclarationSortRank(row.vat_declaration ?? notStartedWithoutDeclaration(row.id))
                    : null,
                customRender: (row: ClientRow) => {
                    if (vatDeclarationState === 'loading') {
                        return <CellSkeleton width={90} />;
                    }
                    if (vatDeclarationState === 'failed') {
                        return (
                            <span className="text-gray-300" title={VAT_DECLARATION_LOAD_FAILED}>
                                <span aria-hidden="true">–</span>
                                <span className="sr-only">{VAT_DECLARATION_LOAD_FAILED}</span>
                            </span>
                        );
                    }
                    return (
                        <VatDeclarationStatusPill
                            clientId={row.id}
                            clientName={row.business_name}
                            declaration={row.vat_declaration ?? notStartedWithoutDeclaration(row.id)}
                        />
                    );
                }
            }] : []),
            {
                name: '',
                field: 'id',
                style: {
                    widthMultiplier: 0.5
                },
                disableSorting: true,
                customRender: (row: ClientRow) => (
                    <div className="flex justify-end items-center">
                        <ClientRowActionsMenu onLoginAsClient={() => logInAsSubordinate(row.id)} />
                    </div>
                )
            },
        ],
        pagination: paginationState ? {
            inMemory: true,
            paginationState: paginationState,
            totalPages: Math.ceil(clientReports.length / 10),
            defaultPageSize: 25,
        } : undefined,
        sorting: {
            sortingState: sortingState,
            inMemory: true
        },
        search: {
            searchFn: (row: ClientRow, searchState: SearchState) => row?.business_name?.toLowerCase().includes(searchState.currentQuery.toLowerCase()),
            filters: filters,
            actions: actions,
            className: "w-80 grow-0"
        },
        onRowClick: onClientSettingsClick,
        options: {
            disableTopPaginationWidget: true,
            disableBottomPaginationWidget: !paginationState,
            allowFullRowClick: true,
        }
    }

    const TableComponent = createTable<ClientRow>(clientReports, tableSpec);
    return (
        <>
            {TableComponent()}
        </>
    )
}
